import type { Ctx } from './ctx';
import type { ViewModel } from '@/screens/generated/view-model';
import { api } from '@/client/api';

/** Authoritative risk controls (mandate §1) — the model half of the Prepare risk cards and the Risk Rules
 *  screen, now reading and writing the SERVER's own state.
 *
 *  The law this slice keeps: what a card displays is what the funnel enforces, because both come from the same
 *  profile version. Every write goes through `prepare.riskProfile.update` / `prepare.riskRules.update` and the
 *  UI reconciles from the RESPONSE — the server's read-back — never from an optimistic local flip. A weakening
 *  comes back as a PENDING change (refractory-delayed, ADR-014) and the card says so instead of showing the
 *  looser value early; a strengthening comes back applied.
 */

/** What toggling a control ON restores when the server holds 'Not set': the design's own value where the
 *  package shipped one, else the control's first parameter. Never invented per render — one table, stated. */
function defaultValueFor(meta: { value: string; params?: readonly { k: string; v: string }[] }): string {
  if (meta.value && !/^not set$/i.test(meta.value.trim())) return meta.value;
  return meta.params?.[0]?.v ?? 'On';
}

const UNSET = new Set(['', 'not set', 'none', 'off', 'disabled', 'no']);
export const isRiskValueSet = (value: string | undefined): boolean =>
  value !== undefined && !UNSET.has(value.trim().toLowerCase());

/** One profile write, reconciled from the server's answer. The response carries BOTH the applied profile and
 *  the pending queue, so a weakening that was delayed shows as pending in the same breath. Module-level so the
 *  Prepare cards (built in core.ts) and this slice share ONE write path. */
export async function saveRiskValue(c: Ctx, key: string, to: string, label: string): Promise<void> {
  if (!c.S.risk || c.S.risk.busy) return;
  const patch = (part: Partial<NonNullable<Ctx['S']['risk']>>) =>
    c.set((st) => (st.risk ? { risk: { ...st.risk, ...part } } : {}));
  patch({ busy: key, error: null, editor: null });
  try {
    const saved = await api.prepare.riskProfile.update({
      idempotencyKey: `risk-${key}-${Date.now()}`,
      changes: { [key]: to },
    });
    const applied = saved.profile.values[key] === to;
    patch({
      busy: null,
      values: { ...saved.profile.values },
      pending: saved.pending.map((p) => ({ ...p })),
      frozen: saved.profile.frozen,
    });
    c.flash(
      applied
        ? `${label} set to ${to}.`
        : `${label}: the loosening to ${to} is QUEUED — the current limit holds until the delay elapses (ADR-014).`,
      applied ? 'pos' : 'mut',
    );
  } catch (error) {
    patch({ busy: null });
    c.flash(error instanceof Error ? error.message : `${label} could not be saved.`, 'neg');
  }
}

export async function toggleRiskRule(c: Ctx, key: string, enabled: boolean, label: string): Promise<void> {
  if (!c.S.risk || c.S.risk.busy) return;
  const patch = (part: Partial<NonNullable<Ctx['S']['risk']>>) =>
    c.set((st) => (st.risk ? { risk: { ...st.risk, ...part } } : {}));
  patch({ busy: key, error: null });
  try {
    const saved = await api.prepare.riskRules.update({
      idempotencyKey: `rule-${key}-${Date.now()}`,
      key,
      enabled,
    });
    const state = saved.state.find((r) => r.key === key);
    patch({
      busy: null,
      rules: saved.state.map((r) => ({ ...r, params: { ...r.params } })),
      custom: saved.custom.map((r) => ({ ...r })),
    });
    c.flash(
      state?.effectiveAt
        ? `${label}: disabling is QUEUED — the rule stays active until the delay elapses (ADR-014).`
        : `${label} ${enabled ? 'enabled' : 'disabled'}.`,
      state?.effectiveAt ? 'mut' : 'pos',
    );
  } catch (error) {
    patch({ busy: null });
    c.flash(error instanceof Error ? error.message : `${label} could not be changed.`, 'neg');
  }
}

/** One rule-parameter write (the RiskRules row's Edit), reconciled from the catalogue the server answers
 *  with. A loosened parameter comes back QUEUED (refractory) and the row's countdown says so. */
export async function saveRuleParams(
  c: Ctx,
  key: string,
  params: Record<string, string>,
  label: string,
): Promise<void> {
  if (!c.S.risk || c.S.risk.busy) return;
  const patch = (part: Partial<NonNullable<Ctx['S']['risk']>>) =>
    c.set((st) => (st.risk ? { risk: { ...st.risk, ...part } } : {}));
  patch({ busy: key, error: null, ruleEditor: null });
  try {
    const saved = await api.prepare.riskRules.update({
      idempotencyKey: `rule-params-${key}-${Date.now()}`,
      key,
      params,
    });
    const state = saved.state.find((r) => r.key === key);
    const appliedAll = Object.entries(params).every(([k, v]) => state?.params[k] === v);
    patch({
      busy: null,
      rules: saved.state.map((r) => ({ ...r, params: { ...r.params } })),
      custom: saved.custom.map((r) => ({ ...r })),
    });
    c.flash(
      appliedAll
        ? `${label} parameters saved.`
        : `${label}: the loosening is QUEUED — the current parameters hold until the delay elapses (ADR-014).`,
      appliedAll ? 'pos' : 'mut',
    );
  } catch (error) {
    patch({ busy: null });
    c.flash(error instanceof Error ? error.message : `${label} could not be saved.`, 'neg');
  }
}

export function buildRisk(c: Ctx) {
  const S = c.S;
  const R = S.risk;

  const patch = (part: Partial<NonNullable<typeof R>>) =>
    c.set((st) => (st.risk ? { risk: { ...st.risk, ...part } } : {}));

  const load = async () => {
    if (S.risk?.loaded || S.risk?.busy === '__load__') return;
    c.set({
      risk: {
        loaded: false,
        busy: '__load__',
        error: null,
        values: {},
        rules: [],
        custom: [],
        pending: [],
        frozen: false,
        editor: null,
        ruleEditor: null,
        customEditor: null,
      },
    });
    try {
      const [profile, catalogue, pending] = await Promise.all([
        api.prepare.riskProfile.get(),
        api.prepare.riskRules.catalogue(),
        api.prepare.access.pendingChanges(),
      ]);
      c.set({
        risk: {
          loaded: true,
          busy: null,
          error: null,
          values: { ...profile.values },
          rules: catalogue.state.map((r) => ({ ...r, params: { ...r.params } })),
          custom: catalogue.custom.map((r) => ({ ...r })),
          pending: pending.items.map((p) => ({ ...p })),
          frozen: profile.frozen,
          editor: null,
          ruleEditor: null,
          customEditor: null,
        },
      });
    } catch (error) {
      c.set((st) =>
        st.risk
          ? {
              risk: {
                ...st.risk,
                busy: null,
                error: error instanceof Error ? error.message : 'Risk settings could not be loaded.',
              },
            }
          : {},
      );
    }
  };

  const saveValue = (key: string, to: string, label: string) => saveRiskValue(c, key, to, label);
  const toggleRule = (key: string, enabled: boolean, label: string) => toggleRiskRule(c, key, enabled, label);

  const pendingFor = (key: string) => R?.pending.find((p) => p.key === key) ?? null;

  return {
    riskLoad: () => void load(),
    riskState: R,
    riskFrozen: R?.frozen ?? false,
    riskEditorOpen: R?.editor ?? null,
    riskEditorSet: (value: string) =>
      c.set((st) => (st.risk?.editor ? { risk: { ...st.risk, editor: { ...st.risk.editor, value } } } : {})),
    riskEditorCancel: () => patch({ editor: null }),
    riskEditorSave: () => {
      const e = R?.editor;
      if (!e) return;
      if (!e.value.trim()) {
        c.flash('Give the limit a value, or toggle the control off instead.', 'neg');
        return;
      }
      void saveValue(e.key, e.value.trim(), e.name);
    },
    riskValueEdit: (key: string, name: string) =>
      patch({ editor: { key, name, value: R?.values[key] ?? '' } }),
    riskToggleControl: (
      key: string,
      name: string,
      meta: { value: string; params?: readonly { k: string; v: string }[] },
    ) => {
      const current = R?.values[key];
      const on = isRiskValueSet(current);
      void saveValue(key, on ? 'Not set' : defaultValueFor(meta), name);
    },
    riskToggleRule: toggleRule,
    riskPendingFor: pendingFor,
    /* Rule param editor (the row's Edit) */
    ruleParamEditorOpen: R?.ruleEditor ?? null,
    ruleParamEdit: (key: string, name: string, params: { k: string; v: string }[]) =>
      patch({ ruleEditor: { key, name, params: params.map((p) => ({ ...p })) } }),
    ruleParamSet: (k: string, v: string) =>
      c.set((st) =>
        st.risk?.ruleEditor
          ? {
              risk: {
                ...st.risk,
                ruleEditor: {
                  ...st.risk.ruleEditor,
                  params: st.risk.ruleEditor.params.map((p) => (p.k === k ? { ...p, v } : p)),
                },
              },
            }
          : {},
      ),
    ruleParamCancel: () => patch({ ruleEditor: null }),
    ruleParamSave: () => {
      const e = R?.ruleEditor;
      if (!e) return;
      if (e.params.some((p) => !p.v.trim())) {
        c.flash('Give every parameter a value, or cancel.', 'neg');
        return;
      }
      void saveRuleParams(c, e.key, Object.fromEntries(e.params.map((p) => [p.k, p.v.trim()])), e.name);
    },
    /* Custom advisory rules (Add Rule) */
    customRules: (R?.custom ?? []).map((rule) => {
      const t = c.tog(rule.enabled);
      return {
        ...rule,
        trackBg: t.trackBg,
        knob: t.knob,
        knobBg: t.knobBg,
        severityLabel: rule.severity === 'hard' ? 'Advisory · strong' : 'Advisory',
        status: rule.enabled ? 'Active' : 'Disabled',
        statusFg: rule.enabled ? 'var(--pos)' : 'var(--muted2)',
        statusBd: rule.enabled ? 'color-mix(in srgb, var(--pos) 35%, transparent)' : 'var(--l12)',
        firedNote: rule.lastFiredAt ? `Last fired ${rule.lastFiredAt.slice(0, 10)}` : 'Has not fired yet',
        toggle: () =>
          void api.prepare.riskRules
            .customUpdate({
              idempotencyKey: `crl-${rule.id}-${Date.now()}`,
              id: rule.id,
              enabled: !rule.enabled,
            })
            .then((out) => {
              patch({ custom: out.custom.map((r) => ({ ...r })) });
              c.flash(`${rule.label} ${rule.enabled ? 'disabled' : 'enabled'}.`, 'pos');
            })
            .catch((error: unknown) =>
              c.flash(error instanceof Error ? error.message : `${rule.label} could not be changed.`, 'neg'),
            ),
        remove: () =>
          void api.prepare.riskRules
            .customDelete({ idempotencyKey: `crd-${rule.id}-${Date.now()}`, id: rule.id })
            .then((out) => {
              patch({ custom: out.custom.map((r) => ({ ...r })) });
              c.flash(`${rule.label} deleted.`, 'mut');
            })
            .catch((error: unknown) =>
              c.flash(error instanceof Error ? error.message : `${rule.label} could not be deleted.`, 'neg'),
            ),
      };
    }),
    customRuleAdd: () => {
      if (!R?.loaded) {
        c.flash('Risk rules are still loading from the server — a moment.', 'mut');
        return;
      }
      patch({ customEditor: { label: '', expression: '', severity: 'soft' } });
    },
    customEditorOpen: R?.customEditor ?? null,
    customEditorSet: (field: 'label' | 'expression' | 'severity', value: string) =>
      c.set((st) =>
        st.risk?.customEditor
          ? { risk: { ...st.risk, customEditor: { ...st.risk.customEditor, [field]: value } } }
          : {},
      ),
    customEditorCancel: () => patch({ customEditor: null }),
    customEditorSave: () => {
      const e = R?.customEditor;
      if (!e) return;
      if (!e.label.trim() || !e.expression.trim()) {
        c.flash('Give the rule a name and the condition it should watch for.', 'neg');
        return;
      }
      patch({ customEditor: null, busy: '__custom__' });
      void api.prepare.riskRules
        .customCreate({
          idempotencyKey: `crc-${Date.now()}`,
          label: e.label.trim(),
          expression: e.expression.trim(),
          severity: e.severity,
        })
        .then((out) => {
          patch({ busy: null, custom: out.custom.map((r) => ({ ...r })) });
          c.flash(
            `${e.label.trim()} added — advisory: it surfaces on the trade monitor, it never blocks an order.`,
            'pos',
          );
        })
        .catch((error: unknown) => {
          patch({ busy: null });
          c.flash(error instanceof Error ? error.message : 'The rule could not be added.', 'neg');
        });
    },
    riskCancelPending: (id: string) => {
      void api.prepare.access
        .cancel({ id })
        .then((out) => {
          patch({ pending: out.items.map((p) => ({ ...p })) });
          c.flash('Queued change withdrawn — the current limit stands.', 'mut');
        })
        .catch((error: unknown) =>
          c.flash(
            error instanceof Error ? error.message : 'The queued change could not be withdrawn.',
            'neg',
          ),
        );
    },
  } satisfies Partial<ViewModel>;
}
